<template>
  <Widget
    widgetName="top-routine-completion-time"
    :customParams="customParams"
    @dataFetched="handleDataFetched"
    @locationOptionChange="handleLocationOptionChange"
  >
    <template v-slot:content>
      <div class="d-flex flex-column h-100">
        <p class="ma-0 pa-0 caption blue-grey--text text--darken-2 mx-6">
          {{
            $t('insights.completion_routines', {
              time: siteAverageRoutineCompletionTime,
            })
          }}
        </p>
        <div class="d-flex flex-column h-100 align-center justify-center mt-4 ">
          <div class="w-100 px-3">
            <v-row class="ma-0 pa-0 justify-center">
              <v-tooltip
                bottom
                v-for="routine in routines"
                :key="routine.routineDefinitionId"
              >
                <template v-slot:activator="{ on }">
                  <v-col
                    v-on="on"
                    style="max-width: 173px"
                    :cols="cols"
                    @click="goToRoutine(routine.routineDefinitionId)"
                  >
                    <Impact
                      class="routine-completion-impact px-3"
                      :routine="routine.routineDefinition"
                      :routineDefinitionId="routine.routineDefinitionId"
                      :percentageChange="routine.percentageChange"
                      :metricText="getMetricText(routine.averageCompletionTime)"
                    />
                  </v-col>
                </template>
                {{ $t('insights.view_routine') }}
              </v-tooltip>
            </v-row>
          </div>
        </div>
      </div>
    </template>
  </Widget>
</template>

<script>
import Widget from '../SharedComponents/WidgetBase.vue'
import Impact from '../SharedComponents/RoutinesImpact.vue'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import convert from 'convert'
import mixin from '@/mixins/mixin.vue'
import _ from 'lodash'

export default {
  name: 'RoutinesCompletion',

  mixins: [mixin, trackerMixin],

  components: {
    Widget,
    Impact,
  },

  data() {
    return {
      customParams: { limit: 3 },
      selectedSiteId: null,
      routines: [],
      siteAverageRoutineCompletionTime: null,
      timeUnits: {
        milliseconds: 'ms',
        seconds: 's',
        minutes: 'min',
        hours: 'h',
        days: 'd',
        weeks: 'wk',
        months: 'mo',
        years: 'y',
      },
    }
  },

  computed: {
    cols() {
      if (this.$vuetify.breakpoint.xs) return 12

      const length = this.routines.length

      if ([1, 2].includes(length)) return 6

      return 4
    },
  },

  methods: {
    goToRoutine(routineDefinitionId) {
      this.trackInsightsWidgetNavigate({
        widget: 'top-routine-completion-time',
        action: 'goToRoutine',
        routine: routineDefinitionId,
      })
      this.openInNewTab({
        name: 'routineDefinitionView',
        params: {
          routineId: routineDefinitionId,
        },
      })
    },

    formatNumber(value) {
      return Intl.NumberFormat('en-us', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
    },

    removeTrailingS(str) {
      if (str.endsWith('s')) {
        return str.slice(0, -1)
      }
      return str
    },

    getMetricText(timeInSeconds) {
      if (timeInSeconds === 0) return '0'
      if (timeInSeconds === null) return '-'

      const { time, unit } = this.convertTimeUnits(timeInSeconds, 's')

      // The library that converts to the greatest unit (minutes to weeks for example) always returns the unit in plural, we need to remove the s to properly handle the pluralization and translation with phrase
      const singularUnit = this.removeTrailingS(unit)

      const unitText = this.$t(
        `impact_units_short.${_.snakeCase(singularUnit)}`,
        { count: time }
      )

      return `${this.formatNumber(Number(time))} ${unitText}`
    },

    handleLocationOptionChange(option) {
      this.selectedSiteId = option
    },

    async handleDataFetched(widget) {
      this.routines = [...widget.data.topRoutines]

      this.siteAverageRoutineCompletionTime = this.getMetricText(
        widget.data.averageRoutineCompletionTime
      )
    },

    getKey(object, value) {
      return Object.keys(object).find(key => object[key] === value)
    },

    convertTimeUnits(time, unit) {
      try {
        const conversion = convert(time, unit).to('best')

        let convertedValue = conversion?.quantity
        let convertedUnit = this.getKey({ ...this.timeUnits }, conversion.unit)

        if (
          (convertedUnit === 'd' || convertedUnit === 'days') &&
          convertedValue >= 7 &&
          convertedValue < 30
        ) {
          const week = convert(time, unit).to('weeks')

          convertedValue = week
          convertedUnit = this.getKey({ ...this.timeUnits }, 'wk')
        }

        if (
          (convertedUnit === 'd' || convertedUnit === 'days') &&
          convertedValue >= 30
        ) {
          const month = convert(time, unit).to('months')

          convertedValue = month
          convertedUnit = this.getKey({ ...this.timeUnits }, 'mo')
        }

        return { time: convertedValue, unit: convertedUnit }
      } catch (error) {
        return { time, unit }
      }
    },
  },
}
</script>
<style lang="scss">
.routine-completion-impact {
  border-radius: 4px;
  &:hover {
    background: $color-blue-grey-lighten3;
    cursor: pointer;
  }
}
</style>
